import React, { useCallback, useMemo } from 'react';
import {
  Box,
  ErrorModal,
  FlexList,
  PaginationBar,
  SearchInput,
  SelectDropdown,
  Text,
} from '@common-fe/common-fe';

import { OrganizationTypes, SERVER_ERROR_MODAL_TEXT, SERVER_ERROR_MODAL_TITLE } from '@/common/constants';
import { ListItemBase, OptionKey, TableRowOption } from '@/common/types';
import { EmployerStatus, getEmployerStatusOptions } from '@/modules/employer/employer.constants';
import { Employer } from '@/modules/employer/employer.types';
import { useListPagination } from '@/modules/employer/hooks';
import useConfigureOption from '@/modules/employer/hooks/useConfigureOption';
import { stateAllToggling } from '@/utils/handlers/dropdownStateAllToggling';
import { useIsServerError } from '@/utils/hooks/useIsServerError';

import { EmployerLockUnlockModal } from '../../EmployerLockUnlockModal';
import { useLockUnlock } from '../../EmployerLockUnlockModal/hooks';

import { usePartnersList } from './hooks';

const START_PAGE = 1;

interface Props {
  mode: OrganizationTypes;
}
const orgStatusOptions = getEmployerStatusOptions();

const PartnersList: React.FC<Props> = ({ mode }) => {
  const title = useMemo(() => {
    switch (mode) {
      case OrganizationTypes.distributor:
        return 'Distributor';
      case OrganizationTypes.subsidiary:
        return 'Subsidiary';
      default:
        return 'Partner';
    }
  }, [mode]);
  const headers: ListItemBase[] = useMemo(
    () => [
      {
        key: 'Name',
        title: 'Name',
        flex: 2,
      },
      {
        key: 'id',
        title: `${title} ID`,
      },
      {
        key: 'status',
        title: 'Status',
      },
      {
        key: 'statusDate',
        title: 'Status date',
      },
    ],
    [title]
  );
  const {
    page,
    perPage,
    currentSearch,
    filterParams,
    setPage,
    setPerPage,
    searchString,
    setSearchString,
    setFilterParams,
  } = useListPagination();

  const filterParamsValues = useMemo(() => stateAllToggling(filterParams), [filterParams]);

  const hasFilterParams = useMemo(
    () => filterParamsValues.length > 0 && !filterParamsValues.some((param) => param === EmployerStatus.Null),
    [filterParamsValues]
  );

  const { formatedList, list, total, isLoading, error, refetch } = usePartnersList({
    page: page - 1,
    perPage,
    searchString: currentSearch || '',
    status: hasFilterParams ? filterParamsValues.join(', ') : '',
    mode,
  });
  const totalTitle = useMemo(() => {
    if (mode === OrganizationTypes.subsidiary) {
      return `Subsidiaries: ${total || 0}`;
    }

    return `${title}s: ${total || 0}`;
  }, [total, title, mode]);

  const isSubsidiaryMode = mode === OrganizationTypes.subsidiary;

  const employerList = list as unknown as Employer[];
  const configureOption = useConfigureOption(employerList);

  const { lockOption, unlockOption, lockUnlockItem, handleResetLockUnlock } = useLockUnlock(employerList);

  const handleCloseLockUnlockModal = useCallback(() => {
    handleResetLockUnlock();
    refetch();
  }, [handleResetLockUnlock, refetch]);

  const options = useMemo(() => {
    const arrayOfOptions: TableRowOption[] = [...configureOption];
    if (!isSubsidiaryMode) {
      return arrayOfOptions;
    }
    if (lockOption) {
      arrayOfOptions.push(lockOption);
    }
    if (unlockOption) {
      arrayOfOptions.push(unlockOption);
    }
    return arrayOfOptions;
  }, [configureOption, lockOption, unlockOption, isSubsidiaryMode]);

  const { isServerError, setIsServerError } = useIsServerError(error);

  return (
    <Box>
      <FlexList
        testId={`${title}List`}
        headers={headers}
        rows={formatedList}
        total={total}
        pad="spacing24"
        moreCount={perPage}
        loading={isLoading}
        options={options}
        footer={
          <PaginationBar
            page={page}
            testId={`${title}List_pagination`}
            total={total}
            pageSize={perPage}
            onChangePage={setPage}
            onChangePageSize={setPerPage}
          />
        }
      >
        <Box direction="row" justify="between" align="center" pad={{ bottom: 'spacing16' }}>
          <Text margin={{ top: 'spacing8', bottom: 'spacing12' }} weight="bold" color="textBody">
            {totalTitle}
          </Text>
          <Box direction="row" justify="end">
            <SearchInput
              className="search-input"
              testId={`${title}List`}
              value={searchString}
              hasSearchingHistory
              placeholder="Search"
              onChange={setSearchString}
            />
            <Box margin={{ left: 'spacing12' }} width={{ min: 'control', max: 'control' }}>
              <SelectDropdown
                id="status"
                testId={`${title}sList`}
                allMode
                activeTitle
                ellipsisMode
                name="Activity status"
                prefix="Status"
                options={orgStatusOptions}
                values={filterParamsValues}
                onChangeValues={(value: OptionKey[]) => {
                  setPage(START_PAGE);
                  setFilterParams(value);
                }}
              />
            </Box>
          </Box>
        </Box>
      </FlexList>

      <ErrorModal
        testId={`${title}sList_server-error`}
        visible={isServerError}
        header={SERVER_ERROR_MODAL_TITLE}
        helpText={SERVER_ERROR_MODAL_TEXT}
        buttonText="Close"
        buttonTextTryAgain="Try again"
        onSetVisible={setIsServerError}
        onSubmit={refetch}
      />
      {isSubsidiaryMode && (
        <EmployerLockUnlockModal
          visible={!!lockUnlockItem.id}
          employerId={lockUnlockItem.id}
          employerName={lockUnlockItem.title}
          isLockMode={!lockUnlockItem.isLocked}
          onSetVisible={handleCloseLockUnlockModal}
        />
      )}
    </Box>
  );
};

export default PartnersList;
